<script setup lang="ts">
import { ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from '@/i18n'
import { useAuthStore } from '@/stores/auth'

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()

const devEmail = ref('')
const loading = ref(false)
const error = ref('')

// Get redirect URL from query
const redirectUrl = (route.query.redirect as string) || '/dashboard'

async function loginWithGoogle() {
  loading.value = true
  error.value = ''
  try {
    await authStore.startOAuth('google')
  } catch (e) {
    error.value = 'Erreur lors de la connexion Google'
    loading.value = false
  }
}

async function loginWithFacebook() {
  loading.value = true
  error.value = ''
  try {
    await authStore.startOAuth('facebook')
  } catch (e) {
    error.value = 'Erreur lors de la connexion Facebook'
    loading.value = false
  }
}

async function devLogin() {
  if (!devEmail.value) {
    error.value = 'Veuillez entrer un email'
    return
  }
  
  loading.value = true
  error.value = ''
  
  const success = await authStore.devLogin(devEmail.value)
  
  if (success) {
    router.push(redirectUrl)
  } else {
    error.value = 'Erreur lors de la connexion'
  }
  
  loading.value = false
}
</script>

<template>
  <div class="login-view">
    <div class="login-card">
      <div class="login-header">
        <img src="/official_logo_128.png" alt="M2V.io" class="login-logo" />
        <h1>M2V.io</h1>
        <p>Connectez-vous pour créer vos vidéos</p>
      </div>

      <el-alert 
        v-if="error" 
        type="error" 
        :title="error" 
        show-icon 
        closable
        @close="error = ''"
        class="login-alert"
      />

      <div class="login-buttons">
        <el-button 
          size="large" 
          :loading="loading"
          @click="loginWithGoogle"
          class="oauth-btn google"
        >
          <span class="oauth-icon">
            <svg viewBox="0 0 24 24" width="20" height="20">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
          </span>
          {{ t('auth.login_with_google') }}
        </el-button>

      </div>

    </div>
  </div>
</template>

<style scoped>
.login-view {
  min-height: calc(100vh - 64px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--m2v-space-6);
  background: var(--m2v-gradient-glow);
}

.login-card {
  width: 100%;
  max-width: 400px;
  padding: var(--m2v-space-8);
  background: var(--m2v-bg-surface);
  border: 1px solid var(--m2v-border-subtle);
  border-radius: var(--m2v-radius-xl);
}

.login-header {
  text-align: center;
  margin-bottom: var(--m2v-space-8);
}

.login-logo {
  width: 80px;
  height: 80px;
  object-fit: contain;
  display: block;
  margin: 0 auto var(--m2v-space-2);
}

.login-header h1 {
  font-family: var(--m2v-font-display);
  font-size: 1.75rem;
  background: var(--m2v-gradient-hero);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: var(--m2v-space-2);
}

.login-header p {
  color: var(--m2v-text-muted);
}

.login-alert {
  margin-bottom: var(--m2v-space-6);
}

.login-buttons {
  display: flex;
  flex-direction: column;
  gap: var(--m2v-space-3);
}

.oauth-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--m2v-space-3);
}

.oauth-icon {
  display: flex;
  align-items: center;
}

.dev-login {
  margin-top: var(--m2v-space-6);
}

.dev-note {
  text-align: center;
  color: var(--m2v-text-muted);
  font-size: 0.875rem;
  margin-bottom: var(--m2v-space-4);
}

.dev-input {
  margin-bottom: var(--m2v-space-3);
}

.dev-btn {
  width: 100%;
}
</style>
